import { inject } from '@angular/core';
import { MaybeAsync } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { map } from 'rxjs';
import { environment as Config } from '@env/environment';
import { AppRoute, AvailableOutlets } from './app.model';
import {
  loggedOperatorGuard,
  loggedOperatorHasPasswordResetGuard,
  logoutGuard,
  redirectUserHomeGuard
} from '@core/guards/auth-guard';
import { canMatchRole, canMatchRoleDisplay } from '@core/guards/role-guard';
import { AppState } from '@core/state/services/app-state';
import { NgrxStateGuidMgr } from '@core/state/services/ngrx-state-guid-mgr';
import { AssetDetailWrapper } from './features/asset-detail/components/asset-detail-wrapper';
import { canViewCustomerAssets } from './features/assets/guards/assets-guard';
import { CostWrapper } from './features/cost/components/cost-wrapper';
import { ReportWrapperPage } from './features/report/report-wrapper-page';
import { TicketWrapper } from './features/ticket-detail/components/ticket-wrapper';
import { LoaderPage } from '@shared/components/loader/loader-page';
import { NewTicketWrapper } from '@shared/components/ticket/new-ticket-wrapper';
import { filterEnabledRoutes } from '@shared/utility/utility';
import { allOperatorsGuard } from '@core/guards/required-data-guard';
import { AssetChangeStatePage } from '@features/asset-change-state/asset-change-state-page';
import { JobDetailDialogShell } from '@features/job-detail/components/shell/job-detail-dialog-shell';

const titleBuilder = (navbarTitleKey: string): MaybeAsync<string> => {
  inject(AppState).navbarTitleKey.set(navbarTitleKey);
  return inject(TranslocoService)
    .selectTranslate<string>(navbarTitleKey)
    .pipe(map((title) => `Tag Application - ${title}`));
};

const PdfViewer = import('@shared/components/viewer/pdf-viewer').then((c) => c.PdfViewer);

const NotificationEmailTemplate = import('@shared/components/notifications/notification-email-template').then(
  (c) => c.NotificationEmailTemplate
);

export const AppRoutes: AppRoute[] = filterEnabledRoutes([
  {
    path: 'my-profile',
    loadChildren: () => import('./features/my-profile/my-profile.routes').then((m) => m.MyProfileRoutes),
    title: () => titleBuilder('myprofile'),
    data: { showAppLoader: true },
    enableInProd: true,
    canActivate: [loggedOperatorGuard]
  },
  {
    path: 'asset',
    loadChildren: () => import('./features/assets/assets.routes').then((m) => m.AssetsRoutes),
    title: () => titleBuilder('assets'),
    data: { showAppLoader: true, pageCaller: 'Assets' },
    canActivate: [canViewCustomerAssets],
    canMatch: [canMatchRoleDisplay(['my-profile'])],
    enableInProd: true
  },
  {
    path: 'asset',
    component: AssetDetailWrapper,
    providers: [NgrxStateGuidMgr],
    loadChildren: () =>
      import('./features/asset-detail/asset-detail.routes').then((m) =>
        m.AssetDetailRoutes({ outletModal: false, windowOpen: true })
      ),
    data: { showAppLoader: true, outletModal: false },
    enableInProd: true,
    canActivate: [loggedOperatorGuard]
  },
  {
    path: 'asset',
    component: AssetDetailWrapper,
    providers: [NgrxStateGuidMgr],
    loadChildren: () =>
      import('./features/asset-detail/asset-detail.routes').then((m) => m.AssetDetailRoutes({ outletModal: true })),
    data: { showAppLoader: true, outletModal: true },
    enableInProd: true,
    outlet: AvailableOutlets.Modal,
    canActivate: [loggedOperatorGuard, canViewCustomerAssets]
  },
  {
    path: 'asset-direct',
    component: AssetDetailWrapper,
    providers: [NgrxStateGuidMgr],
    loadChildren: () =>
      import('./features/asset-detail/asset-detail.routes').then((m) =>
        m.AssetDetailRoutes({ outletModal: false, windowOpen: true })
      ),
    data: { showAppLoader: true, outletModal: false, windowOpen: true },
    enableInProd: true,
    canActivate: [loggedOperatorGuard, canViewCustomerAssets]
  },
  {
    path: 'asset-change-type',
    outlet: AvailableOutlets.Modal,
    enableInProd: true,
    loadChildren: () =>
      import('./features/asset-change-type/asset-change-type.routes').then((m) =>
        m.AssetChangeTypeRoutes({ outlet: AvailableOutlets.Modal })
      ),
    data: { showAppLoader: true },
    canMatch: [canMatchRole([{ outlets: { modal: null, primary: ['asset'] } }], 'modification', 'Assets')]
  },
  {
    path: 'asset-change-type',
    outlet: AvailableOutlets.SecondModal,
    enableInProd: true,
    loadChildren: () =>
      import('./features/asset-change-type/asset-change-type.routes').then((m) =>
        m.AssetChangeTypeRoutes({ outlet: AvailableOutlets.SecondModal })
      ),
    data: { showAppLoader: false },
    canMatch: [
      canMatchRole([{ outlets: { modal: null, 'second-modal': null, primary: ['asset'] } }], 'modification', 'Assets')
    ]
  },
  {
    path: 'asset-change-state',
    outlet: AvailableOutlets.Modal,
    enableInProd: true,
    component: AssetChangeStatePage,
    loadChildren: () =>
      import('./features/asset-change-state/asset-change-state.routes').then((m) => m.AssetChangeStateRoutes()),
    data: { showAppLoader: true },
    canMatch: [canMatchRole([{ outlets: { modal: null, primary: ['asset'] } }], 'modification', 'Assets')]
  },
  {
    path: 'asset-change-state',
    outlet: AvailableOutlets.SecondModal,
    enableInProd: true,
    component: AssetChangeStatePage,
    loadChildren: () =>
      import('./features/asset-change-state/asset-change-state.routes').then((m) => m.AssetChangeStateRoutes()),
    data: { showAppLoader: false },
    canMatch: [
      canMatchRole([{ outlets: { modal: null, 'second-modal': null, primary: ['asset'] } }], 'modification', 'Assets')
    ]
  },
  {
    path: 'dashboard',
    loadChildren: () => import('./features/dashboard/dashboard.routes').then((m) => m.DashboardRoutes),
    title: () => titleBuilder('dashboard'),
    data: { showAppLoader: true, pageCaller: 'Dashboard' },
    canMatch: [canMatchRoleDisplay(['my-profile'])],
    enableInProd: true
  },
  {
    path: 'job',
    loadChildren: () => import('@features/job-detail/job-detail.routes').then((m) => m.JobDetailRoutes(false)),
    data: { pageCaller: 'Job', moduleName: 'Job' },
    enableInProd: true,
    canMatch: [canMatchRoleDisplay(['my-profile'])]
  },
  {
    path: 'job',
    component: JobDetailDialogShell,
    loadChildren: () => import('@features/job-detail/job-detail.routes').then((m) => m.JobDetailRoutes(true)),
    data: { pageCaller: 'Job', moduleName: 'Job', outletModal: true },
    enableInProd: true,
    outlet: AvailableOutlets.Modal,
    canMatch: [canMatchRoleDisplay(['my-profile'])]
  },
  {
    path: 'monitoring',
    loadChildren: () => import('./features/monitoring/monitoring.routes').then((m) => m.MonitoringRoutes),
    title: () => titleBuilder('monitoring'),
    data: { showAppLoader: true, pageCaller: 'IoT' },
    enableInProd: true,
    canMatch: [canMatchRoleDisplay(['my-profile'])]
  },
  {
    path: 'settings',
    loadChildren: () => import('./features/settings/settings.routes').then((m) => m.SettingsRoutes),
    title: () => titleBuilder('settings'),
    data: { showAppLoader: true, pageCaller: 'Settings' },
    enableInProd: true,
    canMatch: [canMatchRoleDisplay(['my-profile'])]
  },
  {
    path: 'planner',
    loadChildren: () => import('./features/planner/planner.routes').then((m) => m.PlannerRoutes),
    title: () => titleBuilder('planner'),
    data: { showAppLoader: true, pageCaller: 'Planner' },
    canMatch: [canMatchRoleDisplay(['my-profile'])],
    enableInProd: true
  },
  {
    path: 'calendar',
    loadChildren: () => import('./features/calendar/calendar.routes').then((m) => m.CalendarRoutes),
    title: () => titleBuilder('calendar'),
    data: { showAppLoader: true, pageCaller: 'Calendar' },
    enableInProd: true,
    canMatch: [canMatchRoleDisplay(['my-profile'])]
  },
  /* MODULO TICKET */
  {
    path: 'ticket',
    loadChildren: () => import('./features/ticket/ticket.routes').then((m) => m.TICKET_ROUTES),
    title: () => titleBuilder('ticket'),
    data: { showAppLoader: true, pageCaller: 'Ticket' },
    enableInProd: true,
    canMatch: [canMatchRoleDisplay(['my-profile'])]
  },
  /* POPUP NUOVO TICKET */
  {
    path: 'ticket',
    component: NewTicketWrapper,
    children: [
      {
        path: 'new',
        loadComponent: () => import('./features/ticket/views/new-ticket-page').then((c) => c.NewTicketPage),
        canMatch: [allOperatorsGuard()]
      }
    ],
    data: { pageCaller: 'Ticket' },
    enableInProd: true,
    outlet: AvailableOutlets.SecondModal,
    canMatch: [canMatchRoleDisplay(['my-profile'])]
  },
  /* POPUP DETTAGLIO TICKET */
  {
    path: 'ticket',
    component: TicketWrapper,
    loadChildren: () =>
      import('./features/ticket-detail/ticket-detail.routes').then((m) =>
        m.TicketDetailRoutes({ jobUrl: 'job', outletModal: true, isTicketModule: false })
      ),
    data: { pageCaller: 'Ticket' },
    enableInProd: true,
    outlet: AvailableOutlets.Modal,
    canMatch: [canMatchRoleDisplay(['my-profile'])]
  },
  /* POPUP TRASFERISCI / CHIUDI TICKET */
  {
    path: 'ticket/:ticketId',
    loadChildren: () =>
      import('./features/ticket-detail/ticket-detail.routes').then((m) => m.TicketTransferCloseRoutes('second-modal')),
    data: { pageCaller: 'Ticket' },
    enableInProd: true,
    outlet: AvailableOutlets.SecondModal,
    canMatch: [canMatchRoleDisplay(['my-profile'])]
  },
  {
    path: 'items',
    loadChildren: () => import('./features/items/items.routes').then((m) => m.ItemsRoutes),
    title: () => titleBuilder('items'),
    data: { showAppLoader: true, pageCaller: 'Items' },
    enableInProd: true,
    canMatch: [canMatchRoleDisplay(['my-profile'])]
  },
  {
    path: 'cost',
    loadChildren: () => import('./features/cost/cost.routes').then((m) => m.CostRoutes),
    enableInProd: true,
    canActivate: [loggedOperatorGuard]
  },
  {
    path: 'cost',
    component: CostWrapper,
    loadChildren: () => import('./features/cost/cost.routes').then((m) => m.CostRoutes),
    enableInProd: true,
    outlet: AvailableOutlets.Modal,
    canActivate: [loggedOperatorGuard]
  },
  {
    path: 'report',
    loadChildren: () => import('./features/report/report.routes').then((m) => m.ReportRoutes(false)),
    enableInProd: true,
    canActivate: [loggedOperatorGuard]
  },
  {
    path: 'report',
    component: ReportWrapperPage,
    loadChildren: () => import('./features/report/report.routes').then((m) => m.ReportRoutes(true)),
    enableInProd: true,
    outlet: AvailableOutlets.Modal,
    canActivate: [loggedOperatorGuard]
  },
  {
    path: 'report',
    component: ReportWrapperPage,
    loadChildren: () => import('./features/report/report.routes').then((m) => m.ReportRoutes(true)),
    enableInProd: true,
    outlet: AvailableOutlets.SecondModal,
    canActivate: [loggedOperatorGuard]
  },
  {
    path: 'non-compliance',
    loadChildren: () => import('./features/non-compliance/non-compliance.routes').then((m) => m.NonComplianceRoutes),
    title: () => titleBuilder('nonCompliance'),
    data: { showAppLoader: true, pageCaller: 'NonCompliance' },
    enableInProd: true,
    canMatch: [canMatchRoleDisplay(['my-profile'])]
  },
  {
    path: 'consultations',
    loadChildren: () => import('./features/consultations/consultations.routes').then((m) => m.ConsultationsRoutes),
    title: () => titleBuilder('consultations'),
    data: { showAppLoader: true, pageCaller: 'Consultations' },
    enableInProd: true,
    canMatch: [canMatchRoleDisplay(['my-profile'])]
  },
  {
    path: 'import',
    loadChildren: () => import('./features/importer/importer.routes').then((m) => m.ImporterRouting),
    title: () => titleBuilder('imports'),
    data: { showAppLoader: true, pageCaller: 'Import' },
    enableInProd: true,
    canMatch: [canMatchRoleDisplay(['my-profile'])]
  },
  {
    path: 'business-partner',
    loadChildren: () =>
      import('./features/business-partners/business-partner.routes').then((m) => m.BusinessPartnersRoutes),
    title: () => titleBuilder('businessPartner'),
    data: { showAppLoader: true, pageCaller: 'BusinessPartner' },
    enableInProd: true,
    canMatch: [canMatchRoleDisplay(['my-profile'])]
  },
  {
    path: 'pdf-viewer',
    enableInProd: true,
    canActivate: [loggedOperatorGuard],
    children: [
      { path: ':id', loadComponent: () => PdfViewer },
      { path: '', loadComponent: () => PdfViewer }
    ]
  },
  {
    path: 'notification-email',
    enableInProd: true,
    canActivate: [loggedOperatorGuard],
    children: [
      { path: ':pushId', loadComponent: () => NotificationEmailTemplate },
      { path: '', loadComponent: () => NotificationEmailTemplate }
    ]
  },
  {
    path: 'tag-ai',
    outlet: AvailableOutlets.RightPanel,
    enableInProd: true,
    loadChildren: () => import('./features/tag-ai/tag-ai.routes').then((m) => m.TagAiRoutes),
    data: { showAppLoader: false, pageCaller: 'TagAI' },
    canMatch: [canMatchRoleDisplay(['my-profile'])]
  },
  {
    path: 'notifications',
    outlet: AvailableOutlets.RightPanel,
    enableInProd: true,
    loadChildren: () => import('./features/notifications/notifications.routes').then((m) => m.NotificationsRoutes),
    data: { showAppLoader: false }
  },
  {
    path: 'open-house',
    loadChildren: () => import('./features/open-house/open-house.routes').then((m) => m.OpenHouseRoutes),
    enableInProd: true,
    canActivate: [loggedOperatorGuard]
  },
  {
    path: 'monitoring-charts',
    loadChildren: () =>
      import('./features/monitoring-charts-view/monitoring-charts-view.routes').then(
        (m) => m.MonitoringChartsViewRoutes
      ),
    enableInProd: true,
    canActivate: [loggedOperatorGuard]
  },
  {
    path: 'device-items',
    loadChildren: () =>
      import('./features/device-items-view/device-items-view-routes').then((m) => m.DeviceItemsViewRoutes),
    enableInProd: true,
    canActivate: [loggedOperatorGuard]
  },
  {
    path: 'login-error',
    loadComponent: () => import('./features/login/login-failure-page').then((c) => c.LoginFailurePage),
    enableInProd: true
  },
  {
    path: 'reset-password',
    loadComponent: () => import('./features/login/password-reset-page').then((c) => c.PasswordResetPage),
    enableInProd: true,
    canActivate: [loggedOperatorHasPasswordResetGuard]
  },
  {
    path: 'test',
    loadChildren: () => import('./features/test/test.routes').then((m) => m.TestRoutes),
    enableInProd: !Config.production,
    canActivate: [loggedOperatorGuard]
  },
  {
    path: 'demo',
    loadChildren: () => import('./features/demo/demo.routes').then((m) => m.DemoRoutes),
    enableInProd: !Config.production,
    canActivate: [loggedOperatorGuard]
  },
  // Il componente LoaderPage è fittizio in quanto non verrà mai caricato perché il canMatch eseguirà il logout dell'utente
  { path: 'logout', component: LoaderPage, enableInProd: true, canMatch: [logoutGuard] },
  // Il componente LoaderPage è fittizio in quanto non verrà mai caricato perché il canMatch eseguirà il redirect verso la home dell'utente
  { path: '', component: LoaderPage, enableInProd: true, canMatch: [redirectUserHomeGuard] },
  { path: '**', component: LoaderPage, enableInProd: true, canMatch: [redirectUserHomeGuard] } // Route for a 404 page or homePage
]);
